import type { HalalStatus, LeadAssignmentMode, LeadProfileView, LeadTier } from '@/types/api'

/**
 * How a lead's tier and its company profile read on screen.
 *
 * <p>
 * The tier is QuoteHero's rule over what research found, never the model's opinion, so these
 * only label what the API decided. Nothing here re-derives a tier.
 * </p>
 */

export const TIER_LABELS: Record<LeadTier, string> = {
  VeryImportant: 'Very Important',
  Standard: 'Standard',
  Junk: 'Junk',
}

/**
 * Very Important is the one that should catch an eye in a list of thirty, so it takes the hot
 * tone. Standard is the ordinary case and stays quiet; Junk is quieter still - the badge kit
 * has no softer tone than neutral, so the word carries it.
 */
export function tierTone(tier: LeadTier): 'critical' | 'neutral' {
  return tier === 'VeryImportant' ? 'critical' : 'neutral'
}

const SEGMENT_LABELS: Record<string, string> = {
  multi_outlet_fnb: 'Multi-outlet F&B operator',
  franchise: 'Franchise',
  central_kitchen: 'Central kitchen',
  hotel_hospitality: 'Hotel and hospitality',
  single_outlet_fnb: 'Single-outlet F&B',
  retail: 'Retail',
  other_business: 'Other business',
  unknown: 'Unknown',
}

/** A segment as a person reads it. Anything the list does not know is shown as Unknown. */
export function segmentLabel(segment: string | null): string {
  return (segment && SEGMENT_LABELS[segment]) ?? 'Unknown'
}

const HALAL_LABELS: Record<HalalStatus, string> = {
  Certified: 'Certified',
  NotCertified: 'Not certified',
  Unknown: 'Unknown',
}

/** One fact on the Company card: its label, its value, and whether the value is an Unknown. */
export interface ProfileFact {
  label: string
  value: string
  unknown: boolean
  href?: string
}

function yesNo(value: boolean | null): string | null {
  if (value === null) return null
  return value ? 'Yes' : 'No'
}

/**
 * The facts in the order a rep reads them before a call. Unknowns stay in the list rather
 * than disappearing - "Halal: Unknown" is the first question on the call, and a card that hid
 * it would read as though nothing needed asking.
 */
export function profileFacts(profile: LeadProfileView): ProfileFact[] {
  const fact = (label: string, value: string | number | null | undefined, href?: string): ProfileFact => {
    const text = value === null || value === undefined || String(value).trim() === '' ? null : String(value)
    return text === null ? { label, value: 'Unknown', unknown: true } : { label, value: text, unknown: false, href }
  }

  const website = profile.website?.trim() || null
  const websiteHref = website && /^https?:\/\//i.test(website) ? website : website ? `https://${website}` : undefined

  return [
    fact('Registered name', profile.registeredName),
    fact('Website', website, websiteHref),
    fact('Business', profile.businessNature),
    fact('Segment', profile.segment && profile.segment !== 'unknown' ? segmentLabel(profile.segment) : null),
    fact('Outlets', profile.outletCount),
    fact('Franchise', yesNo(profile.isFranchise)),
    fact('Central kitchen', yesNo(profile.hasCentralKitchen)),
    fact('Staff (estimate)', profile.staffBand),
    fact('Revenue (estimate)', profile.revenueBand),
    { label: 'Halal', value: HALAL_LABELS[profile.halalStatus], unknown: profile.halalStatus === 'Unknown' },
    fact('Brands', profile.brands),
  ]
}

/**
 * The Very Important rule is complete enough to save: "same as other leads" and the queue
 * need nothing more, one person needs a person, a rotation needs a team.
 */
export function vipRuleReady(
  mode: LeadAssignmentMode | null,
  userId: string | null,
  teamId: string | null,
): boolean {
  if (mode === 'FixedUser') return !!userId
  if (mode === 'RoundRobin') return !!teamId
  return true
}

/** Only an http(s) address is ever made a link, whatever a source claims to be. */
export function safeSourceHref(url: string): string | null {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null
  } catch {
    return null
  }
}
